// web-common/src/i18n.js — the i18n runtime (D1-DESIGN §8). ICU messages (intl-messageformat
// — the one earned dependency; hand-rolling an ICU parser is a real spec, not a shortcut),
// lazy per-locale bundles, a fallback chain, and the standing rule both SPAs inherit:
// EVERY user-visible string goes through t() — no literals in JSX.
//
//   makeI18n({ registry, loadBundle, initial? }) ->
//     { t, locale(), setLocale, onChange, registry }
//
//   registry   — app/src/i18n/locales.js rows: { tag, nativeName, dir, fallback? }
//   loadBundle — async (tag) => the messages object (the app passes a dynamic import so Vite
//                code-splits each locale into its own chunk; .draft.json files load the same
//                way — a draft SHIPS, marked by its _draft key)
//
// Fallback chain: exact tag -> the registry row's `fallback` (data, not string surgery;
// es-AR names es-ES) -> en-US. A missing key renders the en-US string and console.warns in
// dev — NEVER the raw key to a user.

import { IntlMessageFormat } from 'intl-messageformat';

export function makeI18n({ registry, loadBundle, initial = 'en-US' }) {
  const byTag = new Map(registry.map((r) => [r.tag, r]));
  const bundles = new Map();   // tag -> messages object
  const compiled = new Map();  // `${tag}:${key}` -> IntlMessageFormat
  let active = byTag.has(initial) ? initial : 'en-US';
  const listeners = new Set();
  const warned = new Set();    // format failures warned once per tag:key (drafts are machine-made)
  function warnOnce(id, m) { if (!warned.has(id)) { warned.add(id); console.warn(m); } }

  // compile+format one message; throws on a malformed ICU pattern or a bad arg-set.
  function fmtOne(msg, tag, key, args) {
    const ck = tag + ':' + key;
    let fmt = compiled.get(ck);
    if (!fmt) { fmt = new IntlMessageFormat(msg, tag); compiled.set(ck, fmt); }
    return fmt.format(args);
  }

  function chain(tag) {
    const out = [tag];
    const row = byTag.get(tag);
    if (row && row.fallback && row.fallback !== tag) out.push(row.fallback);
    if (!out.includes('en-US')) out.push('en-US');
    return out;
  }

  async function ensure(tag) {
    if (bundles.has(tag)) return;
    try { bundles.set(tag, (await loadBundle(tag)) || {}); }
    catch (e) {
      console.warn(`i18n: bundle for ${tag} failed to load (${e && e.message}) — falling back`);
      bundles.set(tag, {});
    }
  }

  async function setLocale(tag) {
    if (!byTag.has(tag)) { console.warn(`i18n: unknown locale ${tag} — staying on ${active}`); return; }
    for (const t2 of chain(tag)) await ensure(t2);
    active = tag;
    const row = byTag.get(tag);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = tag;
      document.documentElement.dir = row.dir || 'ltr'; // RTL-safe from day one (none of the 24 are RTL today)
    }
    listeners.forEach((fn) => fn(tag));
  }

  function lookup(key) {
    for (const tag of chain(active)) {
      const b = bundles.get(tag);
      if (b && Object.prototype.hasOwnProperty.call(b, key)) return { tag, msg: b[key] };
    }
    return null;
  }

  function t(key, args = null) {
    const hit = lookup(key);
    if (!hit) {
      console.warn(`i18n: missing key "${key}" in every bundle (locale ${active})`);
      return key; // dev-visible; the en-US bundle is authored complete so this never ships
    }
    if (hit.tag !== active) console.warn(`i18n: "${key}" fell back ${active} -> ${hit.tag}`);
    if (!args) return hit.msg;
    // GUARD: a malformed arg-bearing draft message must never white-screen a locale. Compile+format
    // under try/catch; on failure warn once and fall back to the en-US string for this key (authored
    // complete, so it formats). intl-messageformat is the only thing between a bad draft and a dead app.
    try {
      return fmtOne(hit.msg, hit.tag, key, args);
    } catch (e) {
      warnOnce(hit.tag + ':' + key, `i18n: format failed for "${key}" in ${hit.tag} (${e && e.message}) — using en-US`);
      const en = bundles.get('en-US');
      const enMsg = en && Object.prototype.hasOwnProperty.call(en, key) ? en[key] : null;
      if (enMsg != null && hit.tag !== 'en-US') {
        try { return fmtOne(enMsg, 'en-US', key, args); }
        catch (e2) { warnOnce('en-US:' + key, `i18n: en-US format ALSO failed for "${key}" (${e2 && e2.message})`); }
      }
      return enMsg != null ? enMsg : hit.msg; // last resort: the raw string, never a crash
    }
  }

  return {
    t,
    locale: () => active,
    setLocale,
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    registry,
    ready: setLocale(active), // the boot promise (en-US + chain loaded)
  };
}

// pickInitialLocale(registry, stored) — the selection order (D1-DESIGN §8.2):
// localStorage -> navigator.languages best match (exact, then base-language) -> en-US.
export function pickInitialLocale(registry, stored = null) {
  const tags = registry.map((r) => r.tag);
  if (stored && tags.includes(stored)) return stored;
  const navLangs = (typeof navigator !== 'undefined' && navigator.languages) || [];
  for (const l of navLangs) {
    if (tags.includes(l)) return l;
    const base = String(l).split('-')[0];
    const hit = tags.find((t2) => t2.split('-')[0] === base);
    if (hit) return hit;
  }
  return 'en-US';
}
